import React, { useEffect, useState } from "react";
import { Wrench, Loader2, Power } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const pad = (n) => String(n).padStart(2, "0");

const toInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toIso = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const blank = { on: false, mode: "readonly", backAt: "", startsAt: "", noticeHours: 24, message: "" };

// The switch that takes the site down on purpose, with the time it comes back so nobody has to guess.
export const AdminMaintenance = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(blank);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/maintenance");
    setForm({
      on: !!data.on, mode: data.mode || "readonly",
      backAt: toInput(data.backAt), startsAt: toInput(data.startsAt),
      noticeHours: data.noticeHours ?? 24, message: data.message || "",
    });
    setMeta({ changedAt: data.changedAt, changedBy: data.changedBy, notice: data.notice });
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const save = async (override = {}) => {
    const next = { ...form, ...override };
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance", {
        on: !!next.on, mode: next.mode,
        backAt: toIso(next.backAt), startsAt: toIso(next.startsAt),
        noticeHours: Number(next.noticeHours) || 0, message: next.message || "",
      });
      setForm(next);
      toast({ title: data.on ? "Maintenance mode is ON" : "Maintenance mode is off" });
      await load();
    } catch (e) {
      toast({ title: "Could not save", description: e.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="admin-maintenance">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <h3 className="font-bold text-foreground flex items-center gap-2">
            <Wrench size={18} className="text-[#16a34a]" /> Maintenance mode
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Takes the site down on purpose while you update it. Admins keep working through it, and
            everyone else sees a countdown to the time you set.
          </p>
        </div>
        <button onClick={() => save({ on: !form.on })} disabled={busy} data-testid="maintenance-toggle"
          className={`shrink-0 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-60 ${form.on ? "bg-rose-600 hover:bg-rose-700" : "bg-[#16a34a] hover:bg-[#15803d]"}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />}
          {form.on ? "Bring the site back" : "Take the site down"}
        </button>
      </div>

      {form.on && (
        <p className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs font-bold text-rose-600 dark:text-rose-300" data-testid="maintenance-live-note">
          Live now — {form.mode === "full" ? "shoppers and shops are locked out" : "browsing works, posting and payments are blocked"}.
        </p>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-muted-foreground space-y-1.5">
          <span>What visitors can do</span>
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}
            data-testid="maintenance-mode"
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground">
            <option value="readonly">Read-only — browse, no posting/pay</option>
            <option value="full">Full lockout — countdown only</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-muted-foreground space-y-1.5">
          <span>Back online at (drives the countdown)</span>
          <input type="datetime-local" value={form.backAt} data-testid="maintenance-back-at"
            onChange={(e) => setForm({ ...form, backAt: e.target.value })}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
        </label>
        <label className="text-xs font-semibold text-muted-foreground space-y-1.5">
          <span>Scheduled start (shows a heads-up banner first)</span>
          <input type="datetime-local" value={form.startsAt} data-testid="maintenance-starts-at"
            onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
        </label>
        <label className="text-xs font-semibold text-muted-foreground space-y-1.5">
          <span>Warn this many hours ahead</span>
          <input type="number" min="0" max="168" value={form.noticeHours} data-testid="maintenance-notice-hours"
            onChange={(e) => setForm({ ...form, noticeHours: e.target.value })}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
        </label>
      </div>

      <textarea rows={2} value={form.message} data-testid="maintenance-message-input"
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        placeholder="Optional line shown to everyone (e.g. Upgrading the map. Back by 3am.)"
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => save()} disabled={busy} data-testid="maintenance-save"
          className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-bold px-4 py-2 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : null} Save settings
        </button>
        {meta.notice && (
          <span className="text-xs font-bold text-[#b45309]" data-testid="maintenance-notice-live">Heads-up banner is showing now.</span>
        )}
        {meta.changedAt && (
          <span className="text-xs text-muted-foreground">Last changed {String(meta.changedAt).slice(0, 16).replace("T", " ")} by {meta.changedBy || "admin"}</span>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Staff portal: <span className="font-semibold text-foreground">/staff-login</span> stays open
        during a full lockout so you can sign in as admin while the site is down.
      </p>
    </div>
  );
};

export default AdminMaintenance;
